import React, { createContext, useState, useEffect, useContext } from 'react';
import AuthService from '../services/auth.service';
import FirebaseAuthService from '../services/firebase-auth.service';

// Create the context
const AuthContext = createContext();

// Create a provider component
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize auth state on component mount
  useEffect(() => {
    const initializeAuth = () => {
      try {
        // Check for token first
        const token = localStorage.getItem('token');
        const currentUser = AuthService.getCurrentUser();
        
        if (currentUser && token) {
          console.log('Found existing user in localStorage, restoring session');
          setUser(currentUser);
          
          // Check if this is a recent social sign-up
          const isRecentSignUp = localStorage.getItem('recentSignUp') === 'true';
          if (isRecentSignUp) {
            console.log('Detected recent social sign-up, ensuring user is properly authenticated');
            // Clear the flag after successful initialization
            setTimeout(() => {
              localStorage.removeItem('recentSignUp');
              console.log('Cleared recentSignUp flag after successful authentication');
            }, 2000); // Small delay to ensure other components have initialized
          }
        } else if (currentUser && !token) {
          console.warn('Found user but no token in localStorage, clearing inconsistent state');
          localStorage.removeItem('user');
          setUser(null);
        } else if (!currentUser && token) {
          console.warn('Found token but no user in localStorage, clearing inconsistent state');
          localStorage.removeItem('token');
          setUser(null);
        }
      } catch (err) {
        console.error('Failed to initialize auth state:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    // OPTIMIZATION: Defer auth initialization to allow UI to render first
    // This gives 50-100ms improvement in perceived load time
    setTimeout(() => {
      initializeAuth();
    }, 50);
  }, []);

  // Register a new user
  const register = async (userData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await AuthService.register(userData);
      setUser(response.user);
      return response;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // First step of registration (without verification)
  const registerInitial = async (userData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await AuthService.registerInitial(userData);
      return response;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Send verification code to phone
  const sendVerificationCode = async (phone) => {
    setLoading(true);
    setError(null);
    try {
      const response = await AuthService.sendVerificationCode(phone);
      return response;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Verify phone number with code
  const verifyPhoneNumber = async (phone, code, userId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await AuthService.verifyPhoneNumber(phone, code, userId);
      setUser(response.user);
      return response;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Update phone number and send new verification code
  const updatePhoneNumber = async (phone, userId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await AuthService.updatePhoneNumber(phone, userId);
      return response;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Skip phone verification (for SMS downtime)
  const skipPhoneVerification = async (userId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await AuthService.skipPhoneVerification(userId);
      setUser(response.user);
      return response;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Login user
  const login = async (credentials) => {
    setLoading(true);
    setError(null);
    try {
      const response = await AuthService.login(credentials);
      setUser(response.user);
      return response;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Logout user
  const logout = async () => {
    // Clear user state immediately for instant UI update
    setUser(null);
    setError(null);
    
    try {
      // Handle server-side logout in background
      await AuthService.logout();
      // Also sign out from Firebase
      await FirebaseAuthService.signOut();
    } catch (err) {
      console.error('Background logout error:', err);
      // Don't throw error since local state is already cleared
    }
  };

  // Update user profile
  const updateProfile = async (userData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await AuthService.updateProfile(userData);
      setUser(response.user);
      return response;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Forgot password
  const forgotPassword = async (email) => {
    setLoading(true);
    setError(null);
    try {
      const response = await AuthService.forgotPassword(email);
      return response;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Google login with Firebase
  const googleLogin = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await FirebaseAuthService.googleSignIn();
      if (response && response.user) {
        // Set the user in context
        setUser(response.user);
        console.log('User set in AuthContext after Google login:', response.user);
        
        // Verify the user is properly set in localStorage
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          console.log('User found in localStorage after Google login');
        } else {
          console.warn('User not found in localStorage after Google login, setting it now');
          localStorage.setItem('user', JSON.stringify(response.user));
        }
      } else {
        console.warn('No user data in response from Firebase Google Sign-In');
      }
      return response;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  // Apple login with Firebase
  const appleLogin = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await FirebaseAuthService.appleSignIn();
      if (response && response.user) {
        // Set the user in context
        setUser(response.user);
        console.log('User set in AuthContext after Apple login:', response.user);
        
        // Verify the user is properly set in localStorage
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          console.log('User found in localStorage after Apple login');
        } else {
          console.warn('User not found in localStorage after Apple login, setting it now');
          localStorage.setItem('user', JSON.stringify(response.user));
        }
      } else {
        console.warn('No user data in response from Firebase Apple Sign-In');
      }
      return response;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  // Handle Firebase redirect result (for mobile auth flows)
  const handleRedirectResult = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await FirebaseAuthService.handleRedirectResult();
      if (response) {
        setUser(response.user);
      }
      return response;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Reset password
  const resetPassword = async (resetData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await AuthService.resetPassword(resetData);
      return response;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Context value
  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    register,
    registerInitial,
    sendVerificationCode,
    verifyPhoneNumber,
    updatePhoneNumber,
    skipPhoneVerification,
    login,
    logout,
    updateProfile,
    forgotPassword,
    googleLogin,
    appleLogin,
    resetPassword,
    handleRedirectResult,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use the auth context
const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Export the context as default and the hook and provider as named exports
export default AuthContext;
export { useAuth, AuthProvider };
